import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Alert,
  InputAdornment,
  Divider,
  Collapse,
  ListItemButton,
  Checkbox,
  ListItemAvatar
} from '@mui/material';
import {
  Work as WorkIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircleOutline
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { deletePosition, getPositions, putPosition } from '../../../lib/connector/positionQueries';
import { getUser } from '../../../lib/connector/userQueries';
import { useToast } from '../ToastProvider/ToastProvider';
import { formatPhoneNumber } from '../SmsConsentModal/SmsConsentModal';
import { getUserPool } from '../../../lib/pools/pools';

interface ManagePositionsModalProps {
  open: boolean;
  onClose: () => void;
  selectedSchedule: any;
  userData: any;
}

export default function ManagePositionsModal({
  open,
  onClose,
  selectedSchedule,
  userData
}: ManagePositionsModalProps) {
  const toast = useToast();
  const [positions, setPositions] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [newPositionName, setNewPositionName] = useState('');
  const [editingPosition, setEditingPosition] = useState<any>(null);
  const [expandedPosition, setExpandedPosition] = useState<string | null>(null);
  const [, setLoading] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState<Record<string, any> | null>(null);
  const [confirmDeleting, setConfirmDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      loadPositions();
      loadEmployees();
    }
  }, [open, selectedSchedule]);

  const loadPositions = async () => {
    setLoading(true);
    try {
      if (selectedSchedule) {
        getPositions(selectedSchedule.scheduleId).then((response) => {
          setPositions(response);
        })
      }

    } catch (error: any) {
      toast.showError('Error loading positions');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {

      if (selectedSchedule) {
        let employeeData: any[] = [userData]
        if (selectedSchedule.employees && selectedSchedule.employees.length > 0) {
          await Promise.all(selectedSchedule.employees.map(async (userId: string) => {
            await getUser(userId).then(async (response: any[]) => {
              employeeData.push(response[0])
            })
          }))
          setEmployees(employeeData);
        } else {
          setEmployees(employeeData);
        }
      }
    } catch (error: any) {
      toast.showError('Error loading employees');
    }
  };

  const handleAddPosition = async () => {
    if (!newPositionName.trim()) return;

    if (newPositionName.trim().toLowerCase() === "admin") {
      toast.showError('Cannot add position with reserved name "Admin".');
      return;
    }

    try {
      const newPosition = {
        scheduleId: selectedSchedule.scheduleId,
        position: uuidv4(),
        positionName: newPositionName,
        employees: []
      };

      putPosition(selectedSchedule.scheduleId, newPosition.position, newPositionName).then(() => {
        toast.showSuccess('Position added successfully.');
        setPositions([...positions, newPosition]);
        setNewPositionName('');
      })
    } catch (error: any) {
      toast.showError('Error adding position');
    }
  };

  const handleUpdatePosition = async () => {
    if (!editingPosition || !editingPosition.positionName.trim()) return;

    if (editingPosition.positionName.trim().toLowerCase() === "admin") {
      toast.showError('Cannot rename position to reserved name "Admin".');
      return;
    }

    try {
      await putPosition(selectedSchedule.scheduleId, editingPosition.position, editingPosition.positionName, editingPosition.employees)
      toast.showSuccess('Position updated successfully.');
      setPositions(positions.map(pos => {
        return pos.position === editingPosition.position ? editingPosition : pos;
      }));
      setEditingPosition(null);
    } catch (error: any) {
      toast.showError('Error updating position');
    }
  };

  const handleDeletePosition = async (position: Record<string, any>) => {
    try {
      await deletePosition(selectedSchedule.scheduleId, position.position)
      toast.showSuccess('Position deleted successfully.');
      setPositions(positions.filter(positionTemp => positionTemp.position !== position.position));
    } catch (error: any) {
      toast.showError('Error deleting position');
    }
  };

  const handleRequestDeletePosition = (position: Record<string, any>) => {
    setPositionToDelete(position);
  };

  const handleCancelDelete = () => {
    setPositionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!positionToDelete) return;
    if (positionToDelete.positionName === 'Admin') {
      toast.showError('Cannot delete reserved position "Admin".');
      setPositionToDelete(null);
      return;
    }
    setConfirmDeleting(true);
    try {
      await handleDeletePosition(positionToDelete);
      setPositionToDelete(null);
    } finally {
      setConfirmDeleting(false);
    }
  };

  const handleToggleEmployeePosition = async (employeeId: string, position: Record<string, any>) => {
    try {
      let newEmployees = position.employees
      if (newEmployees.includes(employeeId)) {
        newEmployees = newEmployees.filter((id: string) => id !== employeeId)
      } else {
        newEmployees.push(employeeId)
      }
      await putPosition(selectedSchedule.scheduleId, position.position, position.positionName, newEmployees)

      setPositions(
        await Promise.all(positions.map((position2) => {
          if (position2.position === position.position) {
            position.employees = newEmployees
          }
          return position2
        }))
      );
    } catch (error: any) {
      toast.showError('Error changing employee position');
    }
  };

  const getUnassignedEmployees = () => {
    let employeesTemp = [...employees];
    positions.map((pos) => {
      pos.employees.map((empId: string) => {
        employeesTemp = employeesTemp.filter(emp => emp?.userId !== empId);
      });
    });
    return employeesTemp;
  };

  const handleClose = () => {
    setNewPositionName('');
    setEditingPosition(null);
    setExpandedPosition(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <WorkIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Manage Positions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {'Current Schedule: ' + (selectedSchedule?.name || 'Current Schedule')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Add New Position */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Add New Position
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Position name (e.g., Cashier, Barista)"
                value={newPositionName}
                onChange={(e) => setNewPositionName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddPosition()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddPosition}
                disabled={!newPositionName.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Existing Positions */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Current Positions ({positions.length})
            </Typography>

            {positions.length === 0 ? (
              <Alert severity="info">
                No positions yet. Add your first position above!
              </Alert>
            ) : (
              <List sx={{ bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                {positions.map((position, index) => (
                  <React.Fragment key={position.position}>
                    <ListItem
                      sx={{
                        py: 2,
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        '&:hover': { bgcolor: 'grey.100' }
                      }}
                    >
                      {editingPosition?.position === position.position ? (
                        // Edit Mode
                        <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder={position.positionName}
                            fullWidth
                            value={editingPosition.positionName}
                            onChange={(e) => setEditingPosition({ ...editingPosition, positionName: e.target.value })}
                            onKeyPress={(e) => e.key === 'Enter' && handleUpdatePosition()}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EditIcon fontSize="small" />
                                </InputAdornment>
                              )
                            }}
                          />
                          <IconButton size="small" color="success" onClick={handleUpdatePosition}>
                            <CheckCircleOutline />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setEditingPosition(null)}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        // View Mode
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: position.color,
                                mr: 2,
                                flexShrink: 0
                              }}
                            />
                            <ListItemText
                              primary={
                                <Typography variant="body1" fontWeight={600}>
                                  {position.positionName}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" fontWeight={600}>
                                  {`${position.employees.length} staff`}
                                </Typography>
                              }
                            />
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedPosition(expandedPosition === position.position ? null : position.position)}
                              >
                                {expandedPosition === position.position ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={position.positionName === "Admin"}
                                onClick={() => setEditingPosition({ ...position })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={position.positionName === "Admin"}
                                onClick={() => handleRequestDeletePosition(position)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Employee Assignment Section */}
                          <Collapse in={expandedPosition === position.position} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 2, pl: 6, pr: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Assign Staff to this Position
                              </Typography>
                              {employees && employees.length > 0 ? <List dense sx={{ borderRadius: 1, border: 1, borderColor: 'divider', maxHeight: 200, overflow: 'auto' }}>
                                {employees.map((employee) => {
                                  return (<ListItemButton
                                    disabled={employee?.userId === selectedSchedule?.userId && position.positionName === "Admin"}
                                    key={employee?.userId}
                                    onClick={() => handleToggleEmployeePosition(employee?.userId, position)}
                                    dense
                                  >
                                    <ListItemAvatar>
                                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: '0.75rem' }}>
                                        {employee?.name.split(' ').map((n: string) => n[0]).join('')}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2">
                                          {employee?.name} {employee?.userId === getUserPool()?.getCurrentUser()?.getUsername()?"(Me)": ""}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography variant="caption" color="text.secondary">
                                          {employee?.email ? employee.email : formatPhoneNumber(employee?.phone)}
                                        </Typography>
                                      }
                                    />
                                    <Checkbox
                                      edge="end"
                                      checked={position.employees.includes(employee?.userId)}
                                      tabIndex={-1}
                                      disableRipple
                                    />
                                  </ListItemButton>)
                                })}
                              </List> : <Typography variant="caption" sx={{ ml:1 }}>No staff available.</Typography>}
                            </Box>
                          </Collapse>
                        </>
                      )}
                    </ListItem>
                    {index < positions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Unassigned Employees */}
          {getUnassignedEmployees().length > 0 && (
            <Alert severity={"warning"}>
              <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                {getUnassignedEmployees().length} staff member{getUnassignedEmployees().length !== 1 ? 's' : ''} without a position:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {getUnassignedEmployees().map(emp => (
                  <Chip
                    label={emp?.name}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                ))}
              </Box>
            </Alert>
          )}

          {/* Info Alert */}
          <Alert severity="info" variant="outlined">
            <Typography variant="caption">
              <strong>Tip:</strong> Click the expand arrow to assign staff to a position.
              Each staff member can only be assigned to multiple position at a time.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      {/* Delete confirmation dialog */}
      <Dialog open={!!positionToDelete} onClose={handleCancelDelete}>
        <DialogTitle>Delete Position</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the position "{positionToDelete?.positionName}"? This cannot be undone and will unassign any staff from this position.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={confirmDeleting}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} variant="contained" disabled={confirmDeleting}>Delete</Button>
        </DialogActions>
      </Dialog>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}